import { useCallback, useMemo } from 'react';
import { skipToken, useResource, useResourceActions } from 'pulse/react';
import { defineLinkOnlyResource } from 'pulse/resources';
import { mergeById } from '../utils/mergeById';
import { getPreviewTicketThread } from './previewMocks';
import { useIsPreview } from '../hooks/useIsPreview';
import { useVisiblePollInterval } from '../hooks/useVisiblePollInterval';
import { GetThreadViewRpc } from '../../__generated__/chirp/com/hubspot/support/portal/rpc/ThreadViewRpc.resources';
import { getPortalAccountId, getPortalInstanceId } from 'portals-platform-lib';
export const getThreadPageParams = ({
  ticketId,
  threadId
}, offset) => ({
  accountId: getPortalAccountId(),
  portalInstanceId: getPortalInstanceId(),
  params: Object.assign({
    ticketId,
    threadId
  }, offset != null ? {
    offsetTimestamp: offset
  } : {})
});
export const toThreadViewData = response => {
  const threadView = response.data;
  if (!threadView) {
    return {
      responders: {},
      attachments: {},
      sensitiveAttachmentsMetaData: {},
      recipients: undefined,
      results: [],
      hasMore: false,
      offset: {
        timeStamp: 0,
        ordinal: 0
      }
    };
  }
  const responders = {};
  (threadView.responders || []).forEach(({
    sender,
    threadViewResponder
  }) => {
    responders[JSON.stringify(sender)] = threadViewResponder;
  });
  return {
    responders,
    attachments: threadView.attachments,
    sensitiveAttachmentsMetaData: threadView.sensitiveAttachmentsMetaData,
    recipients: threadView.recipients,
    results: threadView.results || [],
    hasMore: threadView.hasMore,
    offset: threadView.offset
  };
};
export const mergeThreadViewData = (existing, incoming, isLatestPage = false) => {
  var _incoming$recipients;
  if (!existing) {
    return incoming;
  }
  const mergedResults = mergeById(existing.results || [], incoming.results || []);
  const keepPagination = isLatestPage || existing.hasMore === false;
  return {
    attachments: Object.assign({}, existing.attachments, incoming.attachments),
    responders: Object.assign({}, existing.responders, incoming.responders),
    recipients: (_incoming$recipients = incoming.recipients) !== null && _incoming$recipients !== void 0 ? _incoming$recipients : existing.recipients,
    results: mergedResults,
    hasMore: keepPagination ? existing.hasMore : incoming.hasMore,
    offset: keepPagination ? existing.offset : incoming.offset,
    sensitiveAttachmentsMetaData: Object.assign({}, existing.sensitiveAttachmentsMetaData, incoming.sensitiveAttachmentsMetaData)
  };
};
export const TicketThread = defineLinkOnlyResource({
  typeName: 'customerPortalApp:TicketThread',
  args: ['ticketId', 'threadId']
}).linkTo(GetThreadViewRpc, b => b.findSelf({
  fromOtherParams: ({
    params
  }) => ({
    ticketId: params.params.ticketId,
    threadId: params.params.threadId
  })
}).pullImmutable(({
  self,
  other
}) => mergeThreadViewData(self.data, toThreadViewData(other.data), other.params.params.offsetTimestamp === undefined)).pushBehavior({
  invalidation: false
})).register();
export const useTicketThread = (ticketId, threadId) => {
  const isPreview = useIsPreview();
  const pollInterval = useVisiblePollInterval(isPreview ? undefined : 5000);
  const isSkipped = !ticketId || threadId === undefined;
  const threadParams = {
    ticketId,
    threadId: threadId
  };
  const page = useResource(GetThreadViewRpc, {
    params: isSkipped || isPreview ? skipToken : getThreadPageParams(threadParams),
    pollInterval
  });
  const {
    data: accumulated
  } = useResource(TicketThread, {
    params: isSkipped || isPreview ? skipToken : threadParams,
    passive: true
  });
  const {
    invalidate
  } = useResourceActions(GetThreadViewRpc, {
    params: getThreadPageParams(threadParams)
  });
  const threadData = useMemo(() => isPreview && !isSkipped ? getPreviewTicketThread() : accumulated, [isPreview, isSkipped, accumulated]);
  const hasMore = useMemo(() => {
    var _threadData$hasMore;
    return (_threadData$hasMore = threadData === null || threadData === void 0 ? void 0 : threadData.hasMore) !== null && _threadData$hasMore !== void 0 ? _threadData$hasMore : false;
  }, [threadData]);
  const offset = useMemo(() => {
    var _threadData$offset;
    return threadData === null || threadData === void 0 || (_threadData$offset = threadData.offset) === null || _threadData$offset === void 0 ? void 0 : _threadData$offset.timeStamp;
  }, [threadData]);
  const fetchMore = useCallback(() => {
    if (!hasMore || isPreview) {
      return;
    }
    GetThreadViewRpc.getPromise(getThreadPageParams({
      ticketId,
      threadId: threadId
    }, offset)).catch(() => {});
  }, [hasMore, isPreview, offset, ticketId, threadId]);
  const lastMessageId = useMemo(() => {
    var _threadData$results;
    return threadData === null || threadData === void 0 || (_threadData$results = threadData.results) === null || _threadData$results === void 0 || (_threadData$results = _threadData$results.at(-1)) === null || _threadData$results === void 0 ? void 0 : _threadData$results.id;
  }, [threadData]);
  return {
    data: threadData,
    hasMore,
    refetch: invalidate,
    loading: page.loading && !threadData,
    fetchMore,
    lastMessageId,
    error: page.error
  };
};